<script>
    /** @type {import('./$types').PageData} */
    /** @type {import('./$types').Snapshot<string>} */

    import Fa from 'svelte-fa';
    import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons';

    import CollectionFilter from '$lib/components/CollectionFilter.svelte';
    import CollectionLayoutSwitcher from '$lib/components/CollectionLayoutSwitcher.svelte';
    import CollectionSortBy from '$lib/components/CollectionSortBy.svelte';
    import ProductGrid from '$lib/components/ProductGrid.svelte';
    import ProductList from '$lib/components/ProductList.svelte';

    import { page } from '$app/stores';
    import { browser } from '$app/environment';
    import { afterNavigate, onNavigate } from '$app/navigation';
    import { collectionSchema } from '$lib/utils/structuredData/collectionSchema';

    import Seo from '$lib/components/Seo.svelte';
    import Section from '$lib/components/Section.svelte';
    import SanitySections from '$lib/components/SanitySections.svelte';

    export let data;

    // Check if shopify collection
    const isShopifyCollection = data?.cms?.pageType === 'Shopify Collection' ? true : false;

    $: products = data?.collection?.products?.nodes || [];
    $: pageInfo = data?.collection?.products?.pageInfo || [];
    $: filters = data?.collection?.products?.filters || [];
    $: selectedFilters = data?.selectedFilters || [];
    $: sortKey = data?.sortKey || undefined;

    // set user collection layout preference or use default
    let currentLayout = data?.userPreferences?.collectionLayout || 'list';

    // snapshot for infinite scroll history state
    export const snapshot = {
        capture: () => {
            return { products, pageInfo, scrollPosition: window?.scrollY };
        },
        restore: (value) => {
            products = value.products;
            pageInfo = value.pageInfo;
            setTimeout(() => {
                window.scrollTo(0, value.scrollPosition);
            }, 10);
        }
    };

    // pagination buttons for bots
    const pagination = (direction) => {
        // create href for page nav buttons
        const searchParams = new URLSearchParams($page.url.search);

        switch (direction) {
            case 'next':
                searchParams.delete('before');
                searchParams.set('after', pageInfo?.endCursor);
                break;
            case 'prev':
                searchParams.delete('after');
                searchParams.set('before', pageInfo?.startCursor);
                break;
        }

        return `${$page.url.pathname}?${searchParams.toString()}`;
    };

    // fetch new products on screen for infinite scroll
    let fetchingNewProducts = false;
    const loadMoreProducts = () => {
        if (pageInfo?.hasNextPage) {
            fetchingNewProducts = true;
            let collectionHandle = $page.params.collection;
            let filters = encodeURIComponent(JSON.stringify(data.filters));
            let sortKey = data.sortKey;
            fetch(`/api/collections/${collectionHandle}?after=${pageInfo?.endCursor}&filter=${filters}&sortBy=${sortKey}`)
                .then((r) => r.json())
                .then((nextData) => {
                    // Update the cursor
                    pageInfo = nextData?.collection?.products?.pageInfo;
                    // Merge the product data
                    products = [...products, ...nextData.collection.products.nodes];
                    // Stop loading animation
                    fetchingNewProducts = false;
                })
                .catch((e) => console.error(e));
        }
    };

    // observe user on screen to trigger loadMoreProducts() function
    let observer;
    let loadProductsTrigger;
    const options = { root: null, rootMargin: '0px 0px 50% 0px', threshold: 1 };

    onNavigate(() => {
        if (observer) {
            observer.disconnect();
        }
    });

    afterNavigate(() => {
        if (isShopifyCollection) {
            import('$lib/utils/polyfill/IntersectionObserver').then(() => {
                observer = new IntersectionObserver((entries) => {
                    if (entries[0].isIntersecting && pageInfo?.hasNextPage) {
                        loadMoreProducts();
                    }
                }, options);

                observer.observe(loadProductsTrigger);
            });
        }
    });

    // get strcutured data schema for SEO
    const ldJSON = collectionSchema(data?.collection, data?.cms, $page.url);
</script>

<Seo title={data?.cms?.seo?.title} description={data?.cms?.seo?.description} url={$page.url} {ldJSON} />

<SanitySections sections={data?.cms?.sections} />

{#if isShopifyCollection}
    <Section padding="Top & Bottom" maxWidth="Default" headerVisibile={false}>
        <div class="no-scrollbar -mt-2 mb-4 flex w-full flex-row flex-nowrap items-center gap-2 overflow-y-scroll lg:overflow-y-visible">
            <CollectionFilter {filters} selected={selectedFilters} />
            <div class="relative ml-auto inline-flex gap-2">
                <CollectionSortBy selected={sortKey} />
                <CollectionLayoutSwitcher bind:currentLayout />
            </div>
        </div>

        <div class="{`grid ${currentLayout === 'grid' ? 'grid-cols-2 gap-2 lg:grid-cols-5 lg:gap-4' : 'grid-cols-1 gap-y-4 lg:grid-cols-2 lg:gap-4'}`} ">
            {#each products as product, index (product)}
                <div>
                    {#if currentLayout === 'grid'}
                        <ProductGrid {product} lazyLoadImage={index >= 10} />
                    {:else}
                        <ProductList {product} lazyLoadImage={index >= 10} />
                    {/if}
                </div>
            {/each}
        </div>

        <div class="relative bottom-0 z-10" bind:this={loadProductsTrigger} />

        <div class={`p-7 text-center ${fetchingNewProducts ? 'block' : 'hidden'}`}>
            <Fa icon={faCircleNotch} size="2x" spin={true} class="m-auto text-color-text" />
        </div>

        {#key $page}
            <div class="p-4 text-center">
                {#if !browser}
                    {#if pageInfo?.hasPreviousPage}
                        <a href={pagination('prev')} class="btn">Prev</a>
                    {/if}
                    {#if pageInfo?.hasNextPage}
                        <a href={pagination('next')} class="btn inline-block">Next</a>
                    {/if}
                {/if}
            </div>
        {/key}
    </Section>
{/if}
